import {
  custoDoUso,
  erroDoMotorDaCarga,
  modeloNoOpencode,
  modeloTrocadoDaCarga,
  provedorDoModelo,
  somarCustos,
  TIPO_ERRO_DO_MOTOR,
  TIPO_MODELO_TROCADO,
  TIPO_USO_DO_MOTOR,
} from '@remoto/shared';
import type { ProvedorOpencode } from '@remoto/protocolo';

export interface EventoBruto {
  seq: number;
  tipo: string;
  origem?: string;
  carga: Record<string, unknown>;
  criadoEm?: string;
  /**
   * Quem mandou esta mensagem, quando o servidor sabe (colunas
   * `autor_tipo`/`autor_nome`, 2026-09-21).
   *
   * **Ausente é "não se sabe", nunca "foi você"**: mensagem anterior a este
   * campo, mensagem lida do disco da máquina (o transcript não tem as colunas)
   * e mensagem digitada no editor caem todas aqui. A tela não inventa nome
   * para nenhuma delas — o único rótulo que ela DERIVA é o do editor, e só
   * porque a origem da sessão o afirma.
   */
  autorTipo?: string;
  autorNome?: string;
}

/**
 * Junta o que veio do disco da máquina com o que veio do banco, sem repetir.
 *
 * As duas fontes se sobrepõem de propósito: o banco tem o que o daemon
 * conseguiu enviar, e o disco tem a conversa inteira — inclusive o começo, que
 * costuma estar nos dois. Concatenar sem conferir pintava o início da conversa
 * duas vezes na tela, e foi assim que apareceu na mão do usuário.
 *
 * A chave é o `uuid` da linha do transcript, que é o mesmo dos dois lados. Em
 * empate vence a cópia do banco: ela tem `seq` de verdade, e é por `seq` que a
 * lista se ordena e que o replay do socket continua.
 *
 * Linha sem `uuid` (o `ai-title`, por exemplo) passa dos dois lados: são
 * poucas, não têm identidade estável, e sumir com uma delas seria pior do que
 * mostrar duas.
 */
export function juntarEventos(doDisco: EventoBruto[], doBanco: EventoBruto[]): EventoBruto[] {
  const noBanco = new Set<string>();
  for (const e of doBanco) {
    const uuid = e.carga?.['uuid'];
    if (typeof uuid === 'string' && uuid) noBanco.add(uuid);
  }
  const sobraram = doDisco.filter((e) => {
    const uuid = e.carga?.['uuid'];
    return !(typeof uuid === 'string' && uuid && noBanco.has(uuid));
  });
  return [...sobraram, ...doBanco];
}

/**
 * `claude-fable-5-1` vira `Fable 5.1`; o que não casar com a forma volta como
 * veio — um id cru é feio, mas um nome inventado seria mentira.
 */
export function nomeDoModelo(id: string): string {
  const casou = /^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/.exec(id);
  const familia = casou?.[1];
  const maior = casou?.[2];
  if (!familia || !maior) return id;
  const menor = casou?.[3];
  return familia.charAt(0).toUpperCase() + familia.slice(1) + ' ' + maior + (menor ? '.' + menor : '');
}

export interface TrocaDeModelo {
  de: string;
  para: string;
  /** Em pt-BR, montado aqui: o texto do CLI vem em inglês e não é mostrado cru. */
  motivo: string | null;
}

/**
 * O modelo da sessão trocou SOZINHO — ninguém pediu.
 *
 * Medido em 2026-09-11 na mestre (`eventos_sessao` seq 37745): às 03:33 as
 * salvaguardas do Fable 5.1 recusaram uma mensagem e o próprio CLI caiu para o
 * Opus 4.8. O evento `system`/`model_refusal_fallback` estava gravado, mas o
 * painel descartava todo `system` que não viesse de gancho — a troca aconteceu
 * em silêncio, e a coluna da sessão mudou sem nenhum registro de quem trocou
 * (porque ninguém trocou).
 *
 * A detecção é pelos CAMPOS (`originalModel` → `fallbackModel`), não só pelo
 * subtipo: uma queda futura com outro gatilho, mas a mesma forma, também tem de
 * aparecer. Troca que não troca (`de === para`) não é troca.
 */
export function trocaDeModelo(carga: Record<string, unknown>): TrocaDeModelo | null {
  const de = carga['originalModel'];
  const para = carga['fallbackModel'];
  if (typeof de !== 'string' || typeof para !== 'string' || !de || !para || de === para) {
    return null;
  }
  const categoria = carga['apiRefusalCategory'];
  const motivo =
    carga['trigger'] === 'refusal'
      ? 'as salvaguardas do modelo recusaram a mensagem' +
        (typeof categoria === 'string' && categoria ? ` (categoria: ${categoria})` : '')
      : null;
  return { de: nomeDoModelo(de), para: nomeDoModelo(para), motivo };
}

export type ItemDeChat =
  | {
      chave: string;
      tipo: 'texto';
      autor: 'voce' | 'claude';
      texto: string;
      em?: string;
      /** Entrou na fila do Claude Code e ainda não foi lida por ele. */
      naFila?: boolean;
      /**
       * O nome que a bolha mostra em cima do texto — "Rafael", "Sessão mestre
       * Codex", "Editor".
       *
       * Já é o texto PRONTO, e não um código: quem decide como chamar cada
       * origem é `autoriaDoEvento`, num lugar só. Ausente significa que não se
       * sabe quem mandou, e a bolha não ganha rótulo nenhum.
       */
      autoria?: string;
    }
  | {
      chave: string;
      tipo: 'ferramenta';
      nome: string;
      resumo: string;
      detalhe?: string;
      /** Caminho da imagem que a ferramenta leu ou escreveu, se houver uma. */
      imagem?: string;
      /**
       * Arquivo que a SESSÃO mandou para esta conversa (`enviar_arquivo`).
       *
       * Vem do RESULTADO da ferramenta, não da entrada dela: a chave do objeto
       * é decidida pelo servidor, então antes da volta ela não existe. É o
       * mesmo motivo pelo qual o painel não pode adivinhar isto do texto.
       */
      anexo?: { objeto: string; nome: string; tamanho: number; mime: string };
    }
  | {
      chave: string;
      tipo: 'edicao';
      arquivo: string;
      adicoes: number;
      remocoes: number;
      /** Os dois lados, para o cartão poder mostrar a comparação de verdade. */
      antes: string;
      depois: string;
      /** `true` quando o arquivo foi criado: não há lado esquerdo. */
      criacao: boolean;
    }
  | { chave: string; tipo: 'pensamento'; texto: string }
  | {
      chave: string;
      tipo: 'tarefas';
      /** A lista viva: o TodoWrite seguinte SUBSTITUI, nunca empilha. */
      tarefas: { texto: string; estado: 'pending' | 'in_progress' | 'completed'; ativo?: string }[];
    }
  | {
      chave: string;
      tipo: 'subagente';
      descricao: string;
      subtipo?: string;
      /** Casa com a tarefa viva do stream, que traz o `task_id` para parar. */
      toolUseId?: string;
      /** A ordem que ele recebeu, inteira. A conversa nunca a mostrou. */
      prompt?: string;
      /** O que o subagente fez, passo a passo. Vive na tela de agentes. */
      itens: ItemDeChat[];
      /** O que ele devolveu no fim. Antes caia solto na conversa principal. */
      resultado?: string;
      resultadoComErro?: boolean;
      aberto: boolean;
    }
  | {
      chave: string;
      tipo: 'comando';
      /** O nome com a barra: `/compact`, `/usage`… */
      nome: string;
      argumentos?: string;
      /** O que o comando imprimiu — antes era apagado da tela. */
      saida?: string;
    }
  | { chave: string; tipo: 'sistema'; texto: string }
  | {
      chave: string;
      tipo: 'erro-do-motor';
      /** O texto do motor, PALAVRA POR PALAVRA. A moldura em pt-BR é do cartão. */
      mensagem: string;
      /** O código próprio do motor (`usageLimitExceeded`), quando há um. */
      codigo?: string;
      /** A mensagem não coube inteira e foi cortada na máquina. */
      cortada?: boolean;
      em?: string;
    }
  | ({ chave: string; tipo: 'troca-de-modelo'; em?: string } & TrocaDeModelo)
  | {
      chave: string;
      /**
       * A API trocou o modelo desta conversa do OpenCode porque o provedor
       * dele não é mais o ativo (F2, D46): ela SEGUE no padrão do provedor
       * ativo, e a conversa diz de qual para qual, e por quê.
       */
      tipo: 'modelo-trocado';
      de: string;
      para: string;
      /** O porquê, como a API o escreveu. Ausente: a tela não inventa um. */
      motivo?: string;
      em?: string;
    }
  | {
      chave: string;
      /**
       * A linha que fecha um turno do OpenCode (F17): o custo REAL (R6) e,
       * quando o turno trazia imagem, quem a leu (R8).
       *
       * Vem dos eventos `uso-do-motor` do turno — um por chamada ao modelo —,
       * e não do `turno_fim`: aquele não é gravado nem chega ao painel, e este
       * é, com `seq` e replay. A soma é a mesma (Interface congelada item 12).
       */
      tipo: 'custo-do-turno';
      /** A soma do que o motor RELATOU, em US$; `null` quando nenhuma chamada relatou. */
      custoUsd: number | null;
      /** Alguma chamada do turno veio sem custo: a soma é só do que se sabe. */
      parcial: boolean;
      /** O modelo que leu a imagem do turno, quando não foi o da sessão. */
      imagemLidaPor?: string;
      /**
       * De quem é o custo (F2): no OpenRouter é o COBRADO; no NanoGPT é o
       * EQUIVALENTE em preço de tabela (D45). Ausente quando a conversa não
       * diz o provedor — e aí a linha não afirma nem um nem outro.
       */
      provedor?: ProvedorOpencode;
      /** O turno foi respondido por um modelo incluído na assinatura do NanoGPT (D45). */
      assinatura?: boolean;
    };

interface Bloco {
  type?: string;
  text?: string;
  thinking?: string;
  name?: string;
  id?: string;
  input?: Record<string, unknown>;
  content?: unknown;
  tool_use_id?: string;
  is_error?: boolean;
}

/** O cabeçalho que o Claude Code cola quando o recado vem de outra sessão. */
const MARCA_DE_PEER = 'Another Claude session sent a message:';
const AVISO_DE_PEER = 'This came from another Claude session';

/** Tags que o Claude Code escreve dentro do texto ao rodar um comando local. */
const TAGS_DE_COMANDO =
  /<\/?(command-name|command-message|command-args|local-command-stdout|local-command-caveat|system-reminder)>/g;

/**
 * Tira do texto o que não é conversa.
 *
 * Uma mensagem `user` no transcript carrega três coisas que a pessoa nunca
 * escreveu: as tags de um comando local (`/compact` e amigos), a saída que esse
 * comando imprimiu, e — quando o recado veio de outra sessão — um parágrafo de
 * enquadramento em inglês. Nada disso é errado no transcript; errado é mostrar
 * na bolha. O que sai daqui é o que a pessoa realmente disse.
 *
 * Se o formato mudar numa versão nova do Claude Code, o texto passa inteiro em
 * vez de sumir: é melhor mostrar demais do que engolir a mensagem.
 */
export function limparTextoDoUsuario(bruto: string): {
  texto: string;
  comando: string;
  argumentos: string;
  saida: string;
} {
  let texto = bruto;

  const nome = /<command-name>([^<]*)<\/command-name>/.exec(texto);
  const comando = (nome?.[1] ?? '').trim();
  const args = /<command-args>([\s\S]*?)<\/command-args>/.exec(texto);
  const argumentos = (args?.[1] ?? '').trim();

  // Lembrete interno do Claude Code: nunca foi conversa. Sem tirar daqui, o
  // `rehype-sanitize` comia a tag desconhecida e sobrava uma bolha CLARA
  // VAZIA à direita, como se você tivesse mandado uma mensagem em branco.
  texto = texto.replace(/<system-reminder>[\s\S]*?<\/system-reminder>/g, '');

  // O mesmo vale para a ressalva que o Claude Code cola antes da saída de um
  // comando local ("Caveat: the messages below were generated by the user
  // while running local commands…"). É recado do programa para o modelo, em
  // inglês, e aparecia inteiro na bolha como se você tivesse escrito aquilo.
  texto = texto.replace(/<local-command-caveat>[\s\S]*?<\/local-command-caveat>/g, '');

  // A saída do comando é CAPTURADA, não apagada: `/compact` sem resposta na
  // tela parecia um comando que não fez nada.
  const saidas: string[] = [];
  texto = texto.replace(/<local-command-stdout>([\s\S]*?)<\/local-command-stdout>/g, (_, s: string) => {
    if (s.trim()) saidas.push(s.trim());
    return '';
  });

  texto = texto.replace(/<command-(name|message|args)>[\s\S]*?<\/command-\1>/g, '');
  // Sobra de tag sem par: acontece quando o transcript foi cortado no meio.
  texto = texto.replace(TAGS_DE_COMANDO, '');

  if (texto.includes(MARCA_DE_PEER)) {
    texto = texto.slice(texto.indexOf(MARCA_DE_PEER) + MARCA_DE_PEER.length);
    const aviso = texto.indexOf(AVISO_DE_PEER);
    if (aviso >= 0) texto = texto.slice(0, aviso);
  }

  return { texto: texto.trim(), comando, argumentos, saida: saidas.join('\n').slice(0, 20_000) };
}

function blocosDe(carga: Record<string, unknown>): Bloco[] {
  const mensagem = carga['message'];
  if (!mensagem || typeof mensagem !== 'object') return [];
  const conteudo = (mensagem as { content?: unknown }).content;
  if (typeof conteudo === 'string') return [{ type: 'text', text: conteudo }];
  return Array.isArray(conteudo) ? (conteudo as Bloco[]) : [];
}

function texto(valor: unknown): string {
  if (typeof valor === 'string') return valor;
  if (Array.isArray(valor)) {
    return valor
      .map((b) => (typeof b === 'object' && b !== null ? ((b as Bloco).text ?? '') : String(b)))
      .join('\n');
  }
  return '';
}

/** Uma linha só, para caber no cartão da ferramenta. */
function encurtar(valor: unknown, tamanho = 120): string {
  const bruto = typeof valor === 'string' ? valor : JSON.stringify(valor ?? '');
  const limpo = bruto.replace(/\s+/g, ' ').trim();
  return limpo.length > tamanho ? limpo.slice(0, tamanho) + '…' : limpo;
}

/** O que mostrar de cada ferramenta: o argumento que importa, não o JSON todo. */
/**
 * O caminho da imagem que esta ferramenta tocou, quando ela tocou uma.
 *
 * Ler uma captura de tela e mostrar só o caminho dela é esconder justamente o
 * que a sessão acabou de olhar — quem acompanha fica sem saber se a ferramenta
 * leu a imagem certa. As ferramentas de arquivo do Claude Code guardam o
 * caminho em `file_path`; `notebook_path` e `path` aparecem em outras.
 */
const IMAGEM = /\.(png|jpe?g|gif|webp|avif|bmp)$/i;

/** A ferramenta da sessão mestre que manda arquivo para a conversa. */
export const FERRAMENTA_DE_ANEXO = 'mcp__remoto__enviar_arquivo';

/**
 * O anexo descrito no RESULTADO de `enviar_arquivo`, ou `null`.
 *
 * Nunca lança, por dois motivos que valem igual: o transcript é dado de fora
 * (a regra do parser de JSONL vale aqui também), e uma versão nova do daemon
 * pode mudar o corpo — nesse dia o cartão volta a ser o comum, e a conversa
 * não some da tela.
 *
 * O nome do OBJETO sai da chave, e a chave é montada pelo servidor
 * (`anexos/<sessaoId>/<uuid>.<ext>`). O painel não a inventa nem a completa:
 * se não vier no formato esperado, não há anexo.
 */
export function anexoDoResultado(
  bruto: string,
): { objeto: string; nome: string; tamanho: number; mime: string } | null {
  try {
    const dados = JSON.parse(bruto) as Record<string, unknown>;
    const chave = typeof dados['chave'] === 'string' ? dados['chave'] : '';
    const nome = typeof dados['nome'] === 'string' ? dados['nome'] : '';
    const tamanho = typeof dados['tamanho'] === 'number' ? dados['tamanho'] : 0;
    const mime = typeof dados['mime'] === 'string' ? dados['mime'] : '';
    /*
      A chave INTEIRA é conferida, não só o último pedaço.

      Conferir só o último pedaço parece bastar — a rota de leitura remonta a
      chave a partir do `sessaoId` dela, então travessia não passa. Mas
      `anexos/x/../../segredo.pdf` teria virado um cartão apontando para
      `segredo.pdf`, silenciosamente, e o painel estaria mostrando um arquivo
      que não é o que a sessão mandou. Chave que não tem a forma que o servidor
      gera não é chave: é recusa.
    */
    if (!/^anexos\/[A-Za-z0-9_-]{1,64}\/[A-Za-z0-9-]{1,64}\.[A-Za-z0-9]{1,8}$/.test(chave)) {
      return null;
    }
    const objeto = chave.split('/').pop() ?? '';
    if (nome.length === 0 || tamanho <= 0) return null;
    return { objeto, nome, tamanho, mime };
  } catch {
    return null;
  }
}

export function imagemDaFerramenta(
  nome: string,
  entrada: Record<string, unknown>,
): string | undefined {
  if (!['Read', 'Write', 'Edit', 'NotebookEdit'].includes(nome)) return undefined;
  for (const chave of ['file_path', 'notebook_path', 'path']) {
    const valor = entrada[chave];
    if (typeof valor === 'string' && IMAGEM.test(valor.trim())) return valor.trim();
  }
  return undefined;
}

function resumoDaFerramenta(nome: string, entrada: Record<string, unknown>): string {
  const primeiro = (...chaves: string[]): string => {
    for (const chave of chaves) {
      if (entrada[chave] !== undefined) return encurtar(entrada[chave]);
    }
    return '';
  };
  /** `campo` quando existir, senão vazio — para compor sem `undefined`. */
  const junto = (campo: string, antes = ' · '): string => {
    const valor = entrada[campo];
    return typeof valor === 'string' && valor ? antes + valor : '';
  };

  switch (nome) {
    case 'Bash':
    case 'PowerShell':
      return primeiro('command');
    case 'Read':
    case 'Write':
    case 'Edit':
    case 'MultiEdit':
      return primeiro('file_path', 'notebook_path');
    case 'NotebookEdit':
      return primeiro('notebook_path') + junto('cell_id', ' · célula ');
    case 'Grep':
    case 'Glob':
      return primeiro('pattern');
    case 'WebFetch':
      return primeiro('url');
    case 'WebSearch':
      return primeiro('query');
    case 'Task':
    case 'Agent':
      return primeiro('description', 'prompt') + junto('subagent_type');

    // Execução com corpo próprio: o resumo é a intenção, não o código.
    case 'REPL':
      return primeiro('description', 'code');
    case 'Monitor':
      return primeiro('description', 'command');
    case 'Workflow':
      // `description` e `title` existem no schema mas são IGNORADOS pelo CLI:
      // o nome real vive no bloco `meta` de dentro do script.
      return primeiro('name', 'scriptPath') || 'fluxo definido no script';

    // Sai da máquina ou publica.
    case 'Artifact':
      return (typeof entrada['action'] === 'string' ? entrada['action'] : 'publish') +
        ' · ' + primeiro('title', 'file_path', 'url');
    case 'Projects':
      return primeiro('method') + ' · ' + primeiro('path', 'query');
    case 'RemoteTrigger':
      return primeiro('action') + ' · ' + primeiro('trigger_id', 'session_id');
    case 'PushNotification':
      return primeiro('message');
    case 'SendFeedback':
      return primeiro('type') + junto('title');
    case 'ClaudeDesign':
      return primeiro('operation');

    // Git e agendamento.
    case 'EnterWorktree':
      return primeiro('name', 'path') || 'novo worktree';
    case 'ExitWorktree':
      return entrada['action'] === 'remove' ? 'remover worktree' : 'manter worktree';
    case 'CronCreate':
      return primeiro('cron') + junto('prompt', ' → ');
    case 'CronDelete':
      return primeiro('id');
    case 'CronList':
      return 'listar agendamentos';
    case 'ScheduleWakeup':
      return entrada['stop'] === true
        ? 'encerrar loop'
        : String(entrada['delaySeconds'] ?? '?') + 's' + junto('reason', ' · ');

    // Tarefas do CLI — distintas do TodoWrite.
    case 'TaskStop':
      return primeiro('task_id', 'shell_id');
    case 'TaskOutput':
      return primeiro('task_id') + (entrada['block'] === true ? ' · aguardando' : '');
    case 'TaskCreate':
      return primeiro('subject', 'description');
    case 'TaskGet':
      return primeiro('taskId');
    case 'TaskUpdate':
      return primeiro('taskId') + junto('status', ' → ');
    case 'TaskList':
      return 'listar tarefas';

    // Propostas e achados.
    case 'ProposeGoal':
      return primeiro('condition');
    case 'ProposeSkills':
      return (Array.isArray(entrada['proposals']) ? entrada['proposals'] : [])
        .map((p) => String((p as Record<string, unknown> | null)?.['name'] ?? '?'))
        .join(', ');
    case 'ReportFindings': {
      const achados = Array.isArray(entrada['findings']) ? entrada['findings'].length : 0;
      return `${achados} achado${achados === 1 ? '' : 's'}`;
    }

    // MCP e infraestrutura.
    case 'ListMcpResources':
    case 'RefreshMcpTools':
      return primeiro('server') || 'todos os servidores';
    case 'ReadMcpResource':
    case 'ReadMcpResourceDir':
      return primeiro('server') + junto('uri');

    // As ferramentas da sessão mestre (F13), com resumo que se lê de relance.
    // `primeiro`, e não `junto`: o texto de um despacho pode ser enorme, e o
    // resumo é uma linha — o corpo inteiro fica para o detalhe do cartão.
    case 'mcp__remoto__enviar_mensagem':
      return 'para ' + primeiro('sessaoId') + ' · ' + primeiro('texto');
    case 'mcp__remoto__criar_sessao':
      return primeiro('cwd') + ' · ' + primeiro('primeiraMensagem', 'nome');
    case 'mcp__remoto__buscar':
      return 'busca: ' + primeiro('termo');
    case 'mcp__remoto__ler_conversa':
      return 'conversa ' + primeiro('sessaoId');
    case 'mcp__remoto__ler_config_claude':
      return '.claude de ' + primeiro('maquinaId');

    // O toolkit (F3.7): a rotina, o alvo e a fase — o que se vai rodar onde,
    // em vez do JSON da entrada. Os parâmetros ficam para o detalhe.
    case 'mcp__toolkit__toolkit_executar':
      return primeiro('nome') + ' em ' + primeiro('alvo') + ' · ' + primeiro('fase');
    case 'mcp__toolkit__toolkit_buscar':
      return 'busca no toolkit: ' + primeiro('consulta') + junto('alvo', ' · ');
    case 'mcp__toolkit__toolkit_ler':
      return primeiro('nome') + junto('alvo', ' em ');

    default: {
      // Ferramenta MCP: servidor · ferramenta legível, não o nome-jiboia.
      const mcp = /^mcp__(.+?)__(.+)$/.exec(nome);
      if (mcp) return `${mcp[2]} · ${mcp[1]} — ` + encurtar(entrada, 80);
      return encurtar(entrada);
    }
  }
}

/**
 * O campo que carrega o CORPO da chamada, por ferramenta.
 *
 * Sem isto, o script de um Workflow ou o código de um REPL cai no
 * `JSON.stringify` e a pessoa lê `\n` escapado em vez do texto. O detalhe
 * existe justamente para mostrar a coisa como ela é.
 */
const CORPO_DA_FERRAMENTA: Record<string, string> = {
  Bash: 'command',
  PowerShell: 'command',
  REPL: 'code',
  Workflow: 'script',
  Monitor: 'command',
  NotebookEdit: 'new_source',
  Projects: 'content',
  SendFeedback: 'details',
  Task: 'prompt',
  Agent: 'prompt',
  CronCreate: 'prompt',
  // F13: o despacho do mestre mostra o TEXTO despachado, não JSON escapado.
  mcp__remoto__enviar_mensagem: 'texto',
  mcp__remoto__criar_sessao: 'primeiraMensagem',
};

/**
 * A entrada inteira da ferramenta, para o cartão ter o que abrir.
 *
 * O resumo é uma linha só, com as quebras achatadas — serve para reconhecer a
 * chamada, não para ler. Quem clica quer o comando de verdade, com heredoc e
 * tudo: nada disso é cortado pela máquina, o texto já chega inteiro aqui.
 */
function detalheDaFerramenta(nome: string, entrada: Record<string, unknown>): string {
  const chaveDoCorpo = CORPO_DA_FERRAMENTA[nome];
  const corpo = chaveDoCorpo ? entrada[chaveDoCorpo] : undefined;
  if (typeof corpo === 'string' && corpo.length > 0) {
    // O corpo primeiro, cru; o resto da entrada abaixo, só se houver resto.
    const sobra = Object.fromEntries(
      Object.entries(entrada).filter(([chave]) => chave !== chaveDoCorpo),
    );
    const cauda =
      Object.keys(sobra).length > 0 ? '\n\n--- demais campos ---\n' + JSON.stringify(sobra, null, 2) : '';
    return (corpo + cauda).slice(0, 20_000);
  }
  return (JSON.stringify(entrada, null, 2) ?? '').slice(0, 20_000);
}

/**
 * Linhas trocadas na edição. O Claude Code manda o texto antigo e o novo, não
 * um patch: para o cartão, a conta honesta é quantas linhas saíram e quantas
 * entraram, sem fingir um diff que não temos.
 */
function contarMudancas(antigo: string, novo: string): { adicoes: number; remocoes: number } {
  return {
    adicoes: novo.length > 0 ? novo.split('\n').length : 0,
    remocoes: antigo.length > 0 ? antigo.split('\n').length : 0,
  };
}

/**
 * Tipos do transcript que nunca viram conversa.
 *
 * `mode` entrou em 2026-09-08, com medição: **2921 eventos no banco inteiro,
 * todos com o valor `normal`**, e nenhuma sessão jamais teve dois valores
 * distintos — não existe transição para mostrar. Ele é o `mode` interno do
 * Claude Code, e **não** o modo de permissão do painel, que viaja por
 * `sdk.capacidades` e aparece no selo da lista.
 *
 * O que ele produzia era uma linha de conversa por ocorrência; quando o daemon
 * reinicia, elas chegam em rajada (medido: seis em dois segundos) e afogam a
 * conversa — foi assim que o dono viu catorze `modo: normal` seguidos no
 * celular. A duplicação em si é outra história, do lado do servidor: a chave
 * de idempotência é `(sessaoId, uuidTurno)` e estas linhas não têm uuid.
 * Esconder aqui é conserto de tela, não de dado — o histórico fica intacto.
 */
const RUIDO = new Set(['last-prompt', 'atis-latch', 'frame-link', 'file-history-delta', 'mode']);

/**
 * Transforma o transcript cru em conversa.
 *
 * O formato interno do Claude Code muda entre versões, então nada aqui pode
 * lançar: bloco desconhecido vira uma linha discreta em vez de derrubar a
 * tela inteira de quem só queria acompanhar a sessão.
 */
/** O `todos` do TodoWrite, sem confiar em nada além da forma esperada. */
function tarefasDe(entrada: Record<string, unknown>): ItemDeChat & { tipo: 'tarefas' } {
  const brutas = Array.isArray(entrada['todos']) ? entrada['todos'] : [];
  const tarefas = brutas
    .filter((t): t is Record<string, unknown> => typeof t === 'object' && t !== null)
    .map((t) => ({
      texto: typeof t['content'] === 'string' ? t['content'] : '',
      estado: (['pending', 'in_progress', 'completed'].includes(String(t['status']))
        ? t['status']
        : 'pending') as 'pending' | 'in_progress' | 'completed',
      ...(typeof t['activeForm'] === 'string' ? { ativo: t['activeForm'] } : {}),
    }))
    .filter((t) => t.texto.length > 0);
  return { chave: 'tarefas', tipo: 'tarefas', tarefas };
}

/** Como a conversa é lida: o que a tela sabe além dos eventos. */
export interface OpcoesDosItens {
  /**
   * Esta conversa é do EDITOR — sessão `OBSERVADA`, aberta no VS Code e que
   * nunca passou pelo painel.
   *
   * É a decisão nº 2 do dono: sem pessoa, marca-se a ORIGEM. Aqui ela é
   * derivada, e não carimbada, por uma razão simples — não há o que carimbar:
   * quem digitou no editor não passou por lugar nenhum que soubesse o nome.
   *
   * E ela só é honesta porque a OUTRA metade existe: mensagem que o painel
   * manda para uma sessão observada viaja com autor (o `uuid` do quadro de
   * peer, medido), então ela NÃO cai neste rótulo. Sem aquela metade, todo
   * recado do painel apareceria como se o editor o tivesse escrito.
   */
  doEditor?: boolean;
  /**
   * O modelo da sessão (a coluna `modelo`). É contra ele que o turno com
   * imagem se mede: se quem respondeu foi outro modelo, foi o de visão da
   * frota (R8, `modeloDoTurnoComImagem`) — e a conversa diz. Ausente, a marca
   * não aparece: sem saber o modelo da sessão, "outro modelo" seria chute.
   */
  modeloDaSessao?: string | null;
  /**
   * O provedor de modelos da conversa, quando ela é do OpenCode (F2; sai do id
   * do modelo dela, `provedorDaConversa`). É ele que diz se o custo do turno é
   * o cobrado (OpenRouter) ou o equivalente de tabela (NanoGPT). Uma conversa
   * que a API trocou de provedor (D46) tem os turnos de ANTES do aviso no
   * provedor antigo — o `de` do aviso diz qual.
   */
  provedorOpencode?: ProvedorOpencode | null;
  /**
   * O modelo da sessão está incluído na assinatura do NanoGPT (o `assinatura`
   * do uso da sessão, D45). Vale para os turnos que o PRÓPRIO modelo da sessão
   * respondeu; o turno lido pelo modelo de visão não herda o selo.
   */
  assinaturaDaSessao?: boolean;
}

/**
 * Os ids pelos quais o modelo da sessão aparece no `uso-do-motor`.
 *
 * O daemon grava o `modelID` do OpenCode, que no NanoGPT vem SEM o prefixo da
 * casa (`z-ai/glm-5.3-flash-uncensored`, e não `nano-gpt/z-ai/…`). Comparar só
 * com o id da casa faria todo turno do NanoGPT com imagem dizer "imagem lida
 * por" o próprio modelo da sessão.
 */
function idsDoModeloDaSessao(modelo: string | null): Set<string> {
  if (!modelo) return new Set();
  return new Set([modelo, modeloNoOpencode(modelo).modelID]);
}

/** O turno em curso: o que veio depois da última fala da pessoa. */
interface TurnoEmCurso {
  /** O `seq` do primeiro uso do turno: é a chave estável da linha. */
  primeiroSeq: number | null;
  custos: (number | null)[];
  /** Os modelos que responderam neste turno, na ordem, sem repetir. */
  modelos: string[];
  /** A fala da pessoa que abriu o turno trazia imagem. */
  comImagem: boolean;
  /** O provedor da conversa NESTE turno — antes de um `modelo-trocado`, o antigo. */
  provedor: ProvedorOpencode | null;
  /** A linha já desenhada, para ser trocada — e não empilhada — a cada uso novo. */
  item: Extract<ItemDeChat, { tipo: 'custo-do-turno' }> | null;
}

function novoTurno(comImagem: boolean, provedor: ProvedorOpencode | null): TurnoEmCurso {
  return { primeiroSeq: null, custos: [], modelos: [], comImagem, provedor, item: null };
}

/**
 * A fala da pessoa trazia imagem?
 *
 * No OpenCode a imagem viaja como parte `file` da mensagem, e o daemon a leva
 * em `arquivos` (`mime` e nome, nunca o conteúdo). O bloco `image` fica como
 * segunda leitura, que é a forma do Claude.
 */
function falaComImagem(carga: Record<string, unknown>, blocos: readonly (Bloco | null)[]): boolean {
  const arquivos = carga['arquivos'];
  if (
    Array.isArray(arquivos) &&
    arquivos.some((a) => {
      const mime = typeof a === 'object' && a !== null ? (a as { mime?: unknown }).mime : undefined;
      return typeof mime === 'string' && mime.startsWith('image/');
    })
  ) {
    return true;
  }
  return blocos.some((b) => b?.type === 'image');
}

/**
 * Um evento `user` é FALA da pessoa — e abre um turno — quando traz texto que
 * sobrevive à limpeza ou uma imagem. O resultado de ferramenta também chega
 * como `user` (a forma do Claude, que o OpenCode imita) e não abre nada.
 */
function abreTurno(carga: Record<string, unknown>, blocos: readonly (Bloco | null)[]): boolean {
  const falou = blocos.some(
    (b) =>
      b?.type === 'text' &&
      typeof b.text === 'string' &&
      limparTextoDoUsuario(b.text).texto.length > 0,
  );
  return falou || falaComImagem(carga, blocos);
}

/**
 * A linha do fim do turno, a partir do que ele acumulou — ou `null` quando
 * não há o que dizer (o Codex manda `uso-do-motor` sem dinheiro, e o turno sem
 * imagem não tem marca de visão: nenhum dos dois ganha linha vazia).
 */
function linhaDoTurno(
  turno: TurnoEmCurso,
  modeloDaSessao: string | null,
  assinaturaDaSessao: boolean,
): Extract<ItemDeChat, { tipo: 'custo-do-turno' }> | null {
  const { totalUsd, parcial } = somarCustos(turno.custos);
  const daSessao = idsDoModeloDaSessao(modeloDaSessao);
  const lidaPor =
    turno.comImagem && modeloDaSessao ? turno.modelos.find((m) => !daSessao.has(m)) : undefined;
  if (totalUsd === null && !lidaPor) return null;
  /*
    O selo "assinatura" do turno (D45): só quando TODO modelo que respondeu é o
    da sessão — que é o que o uso diz estar incluído. Turno lido pelo modelo de
    visão ou respondido antes de uma troca não herda o que não se sabe dele.
  */
  const daAssinatura =
    assinaturaDaSessao &&
    turno.provedor !== null &&
    provedorDoModelo(modeloDaSessao ?? '') === turno.provedor &&
    turno.modelos.length > 0 &&
    turno.modelos.every((m) => daSessao.has(m));
  return {
    chave: `${turno.primeiroSeq ?? 0}-turno`,
    tipo: 'custo-do-turno',
    custoUsd: totalUsd,
    parcial,
    ...(lidaPor ? { imagemLidaPor: lidaPor } : {}),
    ...(turno.provedor !== null ? { provedor: turno.provedor } : {}),
    ...(daAssinatura ? { assinatura: true } : {}),
  };
}

/**
 * O provedor dos turnos do COMEÇO da conversa (F2): o `de` do primeiro aviso de
 * troca de provedor, quando houve um (D46) — os turnos de antes dele foram
 * atendidos pelo provedor antigo —, ou o da conversa.
 */
function provedorDoComeco(eventos: readonly EventoBruto[], daConversa: ProvedorOpencode | null): ProvedorOpencode | null {
  for (const evento of eventos) {
    if (evento.tipo !== TIPO_MODELO_TROCADO) continue;
    const troca = modeloTrocadoDaCarga(evento.carga);
    if (troca) return provedorDoModelo(troca.de);
  }
  return daConversa;
}

/**
 * Quem a bolha nomeia, em uma frase.
 *
 * Regra única, e ela nunca adivinha: o que o servidor afirmou vence; a origem
 * da sessão só fala quando o servidor não disse nada; e "não se sabe" continua
 * sem rótulo. Mensagem do modelo nunca ganha nome — o cabeçalho da conversa já
 * diz de qual motor ela é, e repetir isso em toda bolha seria ruído.
 */
export function autoriaDoEvento(
  evento: Pick<EventoBruto, 'autorNome' | 'autorTipo'>,
  opcoes: OpcoesDosItens,
): string | undefined {
  const nome = typeof evento.autorNome === 'string' ? evento.autorNome.trim() : '';
  if (nome) return nome;
  return opcoes.doEditor ? 'Editor' : undefined;
}

export function paraItens(eventos: EventoBruto[], opcoes: OpcoesDosItens = {}): ItemDeChat[] {
  const itens: ItemDeChat[] = [];
  /**
   * O grupo do subagente em curso: enquanto ele vive, todo evento marcado
   * `isSidechain` cai dentro dele em vez de se espalhar pela conversa. O
   * campo já viaja na carga — só nunca tinha sido lido.
   */
  let grupo: { item: Extract<ItemDeChat, { tipo: 'subagente' }>; toolUseId: string } | null = null;
  /** Envios de arquivo esperando o resultado que carrega a chave do objeto. */
  const enviosDeArquivo = new Map<string, Extract<ItemDeChat, { tipo: 'ferramenta' }>>();
  /**
   * O provedor dos turnos daqui em diante (F2). Só existe no OpenCode, e muda
   * no aviso de troca de provedor (D46).
   */
  let provedor = provedorDoComeco(eventos, opcoes.provedorOpencode ?? null);
  /** O custo e o modelo do turno em curso (F17): fecham numa linha depois da resposta. */
  let turno = novoTurno(false, provedor);

  for (const evento of eventos) {
    const { seq, tipo, carga } = evento;
    if (RUIDO.has(tipo)) continue;

    if (tipo === 'ai-title' || tipo === 'custom-title') continue;

    /**
     * Quase todo `attachment` é conversa interna do Claude Code — contador de
     * tokens, resultado de hook, aviso de lote. Num transcript real são
     * milhares, e era isso que enchia a tela de "anexo enviado".
     *
     * Um só é conversa de verdade: `queued_command`, que é **como o recado do
     * painel chega até aqui**. Ele aparece no instante em que o Claude Code
     * enfileira a mensagem, antes de ela virar turno — é o que dá para mostrar
     * "mandei, está esperando" no lugar certo da conversa.
     */
    if (tipo === 'attachment') {
      const anexo = carga['attachment'] as { type?: string; prompt?: unknown } | undefined;
      if (anexo?.type !== 'queued_command') continue;
      /*
        `prompt` chega nas DUAS formas — texto puro e lista de blocos — na
        mesma versão do Claude Code (medido em 27 ocorrências de transcripts
        reais: 13 numa, 14 na outra). Ler só a primeira fazia metade das
        mensagens cair fora em silêncio, e com elas o único registro que a
        conversa tem de um recado mandado no meio do turno: quando o turno
        consome a fila, NENHUM evento `user` nasce depois. Este anexo é a
        mensagem — se ele não vira item, ela não existe para quem olha a tela.
      */
      const pedido = texto(anexo.prompt);
      const { texto: limpo } = limparTextoDoUsuario(pedido);
      if (limpo.length === 0) continue;
      /*
        Sem rótulo de autoria, e de propósito: este anexo é do Claude Code,
        não tem as colunas de autor, e derivar "Editor" dele seria o erro mais
        fácil de cometer — é justamente por AQUI que o recado do painel para
        uma sessão observada aparece primeiro. Some em segundos, quando a
        mensagem de verdade chega com o nome certo.
      */
      itens.push({
        chave: `${seq}-fila`,
        tipo: 'texto',
        autor: 'voce',
        texto: limpo,
        naFila: true,
        ...(evento.criadoEm ? { em: evento.criadoEm } : {}),
      });
      continue;
    }

    /*
      O motor recusou o turno, e a conversa TEM de dizer.

      Este ramo vem antes do corte `user`/`assistant` de propósito: o erro do
      motor não é prosa (ver `erro-do-motor.ts`, no shared), e sem ele aqui
      cairia no descarte silencioso de "tipo que não é conversa" — que é
      exatamente o buraco por onde a cota esgotada do Codex sumiu em
      2026-09-21, deixando a sessão muda sem uma palavra na tela.

      A leitura sai de `erroDoMotorDaCarga`, que é a volta de
      `cargaDoErroDoMotor`, usada pelo daemon. Ler a chave crua aqui seria a
      segunda leitura escrita à mão que diverge da primeira.
    */
    if (tipo === TIPO_ERRO_DO_MOTOR) {
      const erro = erroDoMotorDaCarga(carga);
      // Carga sem mensagem não vira cartão vermelho vazio: ela não diz nada, e
      // um cartão que alarma sem informar é pior que a linha que não apareceu.
      if (!erro) continue;
      itens.push({
        chave: `${seq}-motor`,
        tipo: 'erro-do-motor',
        mensagem: erro.mensagem,
        ...(erro.codigo ? { codigo: erro.codigo } : {}),
        ...(erro.cortada ? { cortada: true } : {}),
        ...(evento.criadoEm ? { em: evento.criadoEm } : {}),
      });
      continue;
    }

    /*
      A API trocou o modelo da conversa porque o provedor dele saiu (F2, D46).

      Como o erro do motor, vem antes do corte `user`/`assistant`: é um tipo
      próprio, que não é prosa (fica fora do índice do cérebro), e sem este
      ramo a troca cairia no descarte silencioso — a conversa mudaria de modelo
      no meio sem ninguém ver por quê. A leitura é a volta do shared
      (`modeloTrocadoDaCarga`), nunca a chave crua.
    */
    if (tipo === TIPO_MODELO_TROCADO) {
      const troca = modeloTrocadoDaCarga(carga);
      if (!troca) continue;
      provedor = provedorDoModelo(troca.para);
      // O turno em curso, se houver, segue o provedor novo daqui em diante.
      turno.provedor = provedor;
      itens.push({
        chave: `${seq}-modelo-trocado`,
        tipo: 'modelo-trocado',
        de: troca.de,
        para: troca.para,
        ...(troca.motivo ? { motivo: troca.motivo } : {}),
        ...(evento.criadoEm ? { em: evento.criadoEm } : {}),
      });
      continue;
    }

    /*
      Uma chamada ao modelo terminou (`uso-do-motor`, um por chamada). O custo
      e o modelo entram no turno em curso, e a linha do fim do turno é
      REDESENHADA no fim da conversa: um turno com ferramenta tem várias
      chamadas, e a linha tem de ficar depois da última resposta, com a soma de
      todas — não uma linha por chamada no meio das bolhas.
    */
    if (tipo === TIPO_USO_DO_MOTOR) {
      const custo = carga['custoUsd'];
      turno.custos.push(custoDoUso({ custoUsd: typeof custo === 'number' ? custo : null }));
      const modelo = carga['model'];
      if (typeof modelo === 'string' && modelo && !turno.modelos.includes(modelo)) {
        turno.modelos.push(modelo);
      }
      turno.primeiroSeq ??= seq;
      if (turno.item) {
        const antes = itens.indexOf(turno.item);
        if (antes >= 0) itens.splice(antes, 1);
      }
      turno.item = linhaDoTurno(turno, opcoes.modeloDaSessao ?? null, opcoes.assinaturaDaSessao === true);
      if (turno.item) itens.push(turno.item);
      continue;
    }

    if (tipo !== 'user' && tipo !== 'assistant') {
      // A troca de modelo que o CLI fez sozinho é o único `system` que vira
      // item: o resto (resumo de gancho, marca de compactação) segue fora.
      if (tipo === 'system') {
        const troca = trocaDeModelo(carga);
        if (troca) {
          itens.push({
            chave: `${seq}-modelo`,
            tipo: 'troca-de-modelo',
            ...troca,
            ...(evento.criadoEm ? { em: evento.criadoEm } : {}),
          });
          continue;
        }
      }
      // A API grava a origem em maiúsculo (`GANCHO`); comparar em minúsculo
      // fazia todo evento de hook sumir em silêncio desde sempre.
      if (String(evento.origem ?? '').toUpperCase() === 'GANCHO') {
        itens.push({ chave: `${seq}-g`, tipo: 'sistema', texto: tipo });
      }
      continue;
    }

    const blocos = blocosDe(carga);
    let indice = 0;

    // Evento de subagente entra no grupo aberto, não na conversa principal.
    const destino = grupo !== null && carga['isSidechain'] === true ? grupo.item.itens : itens;

    // A pessoa falou: começa outro turno, e o custo daqui em diante é dele.
    if (tipo === 'user' && carga['isSidechain'] !== true && abreTurno(carga, blocos)) {
      turno = novoTurno(falaComImagem(carga, blocos), provedor);
    }

    for (const bloco of blocos) {
      indice += 1;
      const chave = `${seq}-${indice}`;
      // Bloco nulo ou de tipo novo não pode derrubar a tela de quem só está
      // acompanhando a sessão: o formato muda entre versões do Claude Code.
      if (!bloco || typeof bloco !== 'object') continue;

      if (bloco.type === 'text' && (bloco.text ?? '').trim().length > 0) {
        const bruto = bloco.text as string;
        // Só a mensagem do usuário carrega tag de comando e recado de peer.
        const { texto: limpo, comando, argumentos, saida } =
          tipo === 'user'
            ? limparTextoDoUsuario(bruto)
            : { texto: bruto, comando: '', argumentos: '', saida: '' };

        if (comando) {
          destino.push({
            chave: chave + '-cmd',
            tipo: 'comando',
            nome: comando.startsWith('/') ? comando : '/' + comando,
            ...(argumentos ? { argumentos } : {}),
            ...(saida ? { saida } : {}),
          });
        }
        if (limpo.length > 0) {
          // A mesma mensagem já apareceu como "na fila" quando o Claude Code a
          // enfileirou. Agora que virou conversa de verdade, a versão em espera
          // sai — senão o mesmo recado fica duas vezes na tela.
          if (tipo === 'user') {
            const espera = itens.findIndex(
              (i) => i.tipo === 'texto' && i.naFila === true && limpo.startsWith(i.texto),
            );
            if (espera >= 0) itens.splice(espera, 1);
          }
          const autoria = tipo === 'user' ? autoriaDoEvento(evento, opcoes) : undefined;
          destino.push({
            chave,
            tipo: 'texto',
            autor: tipo === 'user' ? 'voce' : 'claude',
            texto: limpo,
            ...(autoria ? { autoria } : {}),
            ...(evento.criadoEm ? { em: evento.criadoEm } : {}),
          });
        }
        continue;
      }

      if (bloco.type === 'thinking' || bloco.type === 'redacted_thinking') {
        const pensado = typeof bloco.thinking === 'string' ? bloco.thinking : '';
        if (pensado.trim().length === 0) continue;
        destino.push({ chave, tipo: 'pensamento', texto: pensado });
        continue;
      }

      if (bloco.type === 'tool_use') {
        const nome = bloco.name ?? 'ferramenta';
        const entrada = bloco.input ?? {};

        // A lista de tarefas é VIVA: o TodoWrite seguinte substitui o
        // anterior no lugar, em vez de empilhar cartão sobre cartão.
        if (nome === 'TodoWrite') {
          const nova = tarefasDe(entrada);
          const anterior = destino.findIndex((i) => i.tipo === 'tarefas');
          if (anterior >= 0) destino.splice(anterior, 1);
          if (nova.tarefas.length > 0) destino.push(nova);
          continue;
        }

        // Subagente abre um grupo: o que ele fizer aparece recolhido, não
        // espalhado pela conversa como se fosse a sessão principal.
        if ((nome === 'Task' || nome === 'Agent') && destino === itens) {
          const item: Extract<ItemDeChat, { tipo: 'subagente' }> = {
            chave,
            tipo: 'subagente',
            descricao: encurtar(entrada['description'] ?? entrada['prompt'], 160),
            ...(typeof entrada['subagent_type'] === 'string'
              ? { subtipo: entrada['subagent_type'] }
              : {}),
            ...(bloco.id ? { toolUseId: bloco.id } : {}),
            // O prompt inteiro, e não os 160 caracteres da descrição: é a
            // ordem que o subagente recebeu, e é o que se quer reler quando o
            // resultado dele surpreende.
            ...(typeof entrada['prompt'] === 'string'
              ? { prompt: entrada['prompt'].slice(0, 20_000) }
              : {}),
            itens: [],
            aberto: true,
          };
          itens.push(item);
          grupo = { item, toolUseId: bloco.id ?? chave };
          continue;
        }

        // MultiEdit: um cartão de comparação por edição, agrupadas pela ordem.
        if (nome === 'MultiEdit' && Array.isArray(entrada['edits'])) {
          const arquivo = encurtar(entrada['file_path'], 200);
          let n = 0;
          for (const bruta of entrada['edits'] as unknown[]) {
            n += 1;
            const edicao = (typeof bruta === 'object' && bruta !== null ? bruta : {}) as Record<
              string,
              unknown
            >;
            const antigo = typeof edicao['old_string'] === 'string' ? edicao['old_string'] : '';
            const novo = typeof edicao['new_string'] === 'string' ? edicao['new_string'] : '';
            const { adicoes, remocoes } = contarMudancas(antigo, novo);
            destino.push({
              chave: `${chave}-e${n}`,
              tipo: 'edicao',
              arquivo,
              adicoes,
              remocoes,
              antes: antigo.slice(0, 20_000),
              depois: novo.slice(0, 20_000),
              criacao: false,
            });
          }
          continue;
        }

        if (nome === 'Edit' || nome === 'Write') {
          const arquivo = encurtar(entrada['file_path'], 200);
          const antigo = typeof entrada['old_string'] === 'string' ? entrada['old_string'] : '';
          const novo =
            typeof entrada['new_string'] === 'string'
              ? entrada['new_string']
              : typeof entrada['content'] === 'string'
                ? entrada['content']
                : '';
          const { adicoes, remocoes } = contarMudancas(antigo, novo);
          destino.push({
            chave,
            tipo: 'edicao',
            arquivo,
            adicoes,
            remocoes,
            // Teto de 20 mil caracteres por lado: a comparação existe para a
            // pessoa entender a mudança, não para carregar um arquivo inteiro
            // dentro de uma bolha de conversa.
            antes: antigo.slice(0, 20_000),
            depois: novo.slice(0, 20_000),
            criacao: nome === 'Write',
          });
          continue;
        }

        const imagem = imagemDaFerramenta(nome, entrada);
        const cartao: Extract<ItemDeChat, { tipo: 'ferramenta' }> = {
          chave,
          tipo: 'ferramenta',
          nome,
          resumo: resumoDaFerramenta(nome, entrada),
          detalhe: detalheDaFerramenta(nome, entrada),
          ...(imagem ? { imagem } : {}),
        };
        destino.push(cartao);
        /*
          O envio de arquivo fica esperando o resultado.

          A identidade do anexo (a chave do objeto) só existe DEPOIS da volta —
          quem a escolhe é o servidor, nunca o cliente e nunca o modelo. Por
          isso o cartão é preenchido no `tool_result`, casado por `toolUseId`,
          como o grupo do subagente já fazia.
        */
        if (nome === FERRAMENTA_DE_ANEXO && typeof bloco.id === 'string') {
          enviosDeArquivo.set(bloco.id, cartao);
        }
        continue;
      }

      if (bloco.type === 'tool_result') {
        /*
          O resultado do Task fecha o grupo do subagente — e FICA COM ELE.

          Antes ele era empurrado para a conversa principal como um cartão
          "resultado" solto, cortado em 4000 caracteres e sem nenhuma marca de
          qual subagente o produziu: o grupo já tinha sido fechado uma linha
          acima, então a identidade se perdia ali mesmo. Uma varredura que
          devolve quatrocentas linhas virava quatrocentas linhas no meio da
          conversa de quem só queria saber se ela terminou.
        */
        if (grupo !== null && bloco.tool_use_id === grupo.toolUseId) {
          const conteudoDoTask = texto(bloco.content);
          if (conteudoDoTask.trim().length > 0) {
            grupo.item.resultado = conteudoDoTask.slice(0, 200_000);
            if (bloco.is_error === true) grupo.item.resultadoComErro = true;
          }
          grupo.item.aberto = false;
          grupo = null;
          continue;
        }
        const enviado = bloco.tool_use_id ? enviosDeArquivo.get(bloco.tool_use_id) : undefined;
        if (enviado) {
          enviosDeArquivo.delete(bloco.tool_use_id as string);
          const anexo = anexoDoResultado(texto(bloco.content));
          // Sem anexo o envio falhou (recusa da allowlist, teto de tamanho,
          // armazenamento fora). O cartão comum já mostra a mensagem de erro —
          // inventar um cartão de arquivo aqui seria dizer que subiu.
          if (anexo && bloco.is_error !== true) {
            enviado.anexo = anexo;
            continue;
          }
        }

        const conteudo = texto(bloco.content);
        if (conteudo.trim().length === 0) continue;
        destino.push({
          chave,
          tipo: 'ferramenta',
          nome: bloco.is_error ? 'resultado (erro)' : 'resultado',
          resumo: encurtar(conteudo, 160),
          detalhe: conteudo.slice(0, 4000),
        });
      }
    }
  }

  return itens;
}
